import type { CheckValidationQueueOrderBy } from '@/Network/Types/Requests/CheckValidationRequests'
import {
  CheckRejectionReason,
  CheckValidationEventType,
  CheckValidationStatus
} from '@/Network/Types/Responses/CheckValidationResponses'

/** Default do backend (`CheckValidationQueueQueryDto.limit`). */
export const CHECK_VALIDATION_LIST_LIMIT = 20

/** Espelho de `OVERDUE_DAYS` do backend: `isOverdue` = mais de 5 dias em fila. */
export const CHECK_VALIDATION_OVERDUE_DAYS = 5

/** Mínimo do backend para a observação quando o motivo é `OTHER`. */
export const CHECK_REJECTION_OBSERVATION_MIN_LENGTH = 20

export type CheckValidationPeriodDays = 30 | 90

/** Período default da fila ("Últimos 30 dias" no protótipo). */
export const CHECK_VALIDATION_DEFAULT_PERIOD_DAYS: CheckValidationPeriodDays = 30

/** Status marcado por padrão ao abrir a aba. */
export const CHECK_VALIDATION_DEFAULT_STATUSES: CheckValidationStatus[] = [
  CheckValidationStatus.PENDING_REVIEW
]

export const CHECK_VALIDATION_STATUS_LABELS: Record<CheckValidationStatus, string> = {
  [CheckValidationStatus.NOT_APPLICABLE]: 'Não se aplica',
  [CheckValidationStatus.NOT_EVALUATED]: 'Não avaliado (legado)',
  [CheckValidationStatus.PENDING_REVIEW]: 'Em análise',
  [CheckValidationStatus.APPROVED]: 'Aprovado',
  [CheckValidationStatus.REJECTED]: 'Reprovado'
}

export const CHECK_VALIDATION_EVENT_TYPE_LABELS: Record<CheckValidationEventType, string> = {
  [CheckValidationEventType.SUBMITTED]: 'Enviado para análise',
  [CheckValidationEventType.APPROVED]: 'Aprovado',
  [CheckValidationEventType.REJECTED]: 'Reprovado',
  [CheckValidationEventType.REVERSED]: 'Avaliação revertida',
  [CheckValidationEventType.EDITED_DURING_REVIEW]: 'Editado durante a análise'
}

export const CHECK_REJECTION_REASON_LABELS: Record<CheckRejectionReason, string> = {
  [CheckRejectionReason.ILLEGIBLE_EVIDENCE]: 'Evidência ilegível',
  [CheckRejectionReason.WRONG_STATION]: 'Plantão incorreto',
  [CheckRejectionReason.DIVERGENT_HOURS]: 'Horários divergentes',
  [CheckRejectionReason.INCOMPLETE_EVIDENCE]: 'Evidência incompleta',
  [CheckRejectionReason.REDIGITIZE]: 'Redigitar registro',
  [CheckRejectionReason.FRAUD_SUSPICION]: 'Suspeita de fraude',
  [CheckRejectionReason.OTHER]: 'Outro'
}

/** Formato `{ title, value }` do `v-select` (Vuetify). */
export const CHECK_REJECTION_REASON_OPTIONS = (
  Object.keys(CHECK_REJECTION_REASON_LABELS) as CheckRejectionReason[]
).map((value) => ({ title: CHECK_REJECTION_REASON_LABELS[value], value }))

/**
 * Opções do filtro de status (`SelectFilter`, formato `{ label, value }`).
 * `NOT_APPLICABLE` fica fora: registro que não passa por validação nunca entra
 * na fila. "Evidência indisponível" também não é opção: é estado de
 * visualização (404 no download), sem status persistido para filtrar.
 */
export const CHECK_VALIDATION_STATUS_FILTER_OPTIONS: {
  label: string
  value: CheckValidationStatus
}[] = [
  CheckValidationStatus.PENDING_REVIEW,
  CheckValidationStatus.APPROVED,
  CheckValidationStatus.REJECTED,
  CheckValidationStatus.NOT_EVALUATED
].map((value) => ({ label: CHECK_VALIDATION_STATUS_LABELS[value], value }))

export type CheckValidationQueueTimeFilter = 'overdue' | 'all'

export const CHECK_VALIDATION_QUEUE_TIME_OPTIONS: {
  label: string
  value: CheckValidationQueueTimeFilter
}[] = [
  { label: `Só atrasados (${CHECK_VALIDATION_OVERDUE_DAYS}+ dias)`, value: 'overdue' },
  { label: 'Todos', value: 'all' }
]

export const CHECK_VALIDATION_PERIOD_OPTIONS: {
  label: string
  value: CheckValidationPeriodDays
}[] = [
  { label: 'Últimos 30 dias', value: 30 },
  { label: 'Últimos 90 dias', value: 90 }
]

export const CHECK_VALIDATION_SORT_OPTIONS: {
  label: string
  value: CheckValidationQueueOrderBy
}[] = [
  { label: 'Mais antigos primeiro', value: 'oldest_first' },
  { label: 'Mais recentes primeiro', value: 'newest_first' },
  { label: 'Por unidade', value: 'unit' }
]

/** Rótulo da evidência por tipo de evento (`DoctorCheckEvidence.eventType`). */
export const CHECK_EVIDENCE_EVENT_TYPE_LABELS: Record<string, string> = {
  checkin: 'Entrada',
  checkout: 'Saída',
  'checkin-checkout': 'Entrada e saída'
}
